import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAllActivites } from '@/hooks/queries/useActivites';
import {
  useDeleteChamp,
  useDeleteEtape,
  useDeleteMotifPerte,
  usePipelineChamps,
  usePipelineEtapes,
  usePipelineMotifsPerte,
  useReorderEtapes,
  useUpsertChamp,
  useUpsertEtape,
  useUpsertMotifPerte,
  useUpsertPipeline,
} from '@/hooks/queries/useCrmPipelines';
import { cn } from '@/lib/utils';
import type {
  ChampCustomType,
  EtapeType,
  PipelineChampCustomRow,
  PipelineEtapeRow,
  PipelineMotifPerteRow,
  PipelineRow,
} from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  editing: PipelineRow | null;
}

const STEPS = [
  { key: 'infos', label: 'Infos générales' },
  { key: 'etapes', label: 'Étapes' },
  { key: 'champs', label: 'Champs custom' },
  { key: 'motifs', label: 'Motifs de perte' },
] as const;

const COULEURS_PRESET = [
  '#3b82f6',
  '#8b5cf6',
  '#06b6d4',
  '#0e7490',
  '#15803d',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
];

const CHAMP_TYPES: { value: ChampCustomType; label: string }[] = [
  { value: 'text', label: 'Texte' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'number', label: 'Nombre' },
  { value: 'select', label: 'Liste déroulante' },
  { value: 'multiselect', label: 'Liste multi-choix' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & heure' },
  { value: 'boolean', label: 'Oui / Non' },
  { value: 'url', label: 'URL' },
  { value: 'tier_ref', label: 'Référence tiers' },
  { value: 'user_ref', label: 'Référence utilisateur' },
  { value: 'file', label: 'Fichier' },
];

export function PipelineWizardDialog({
  open,
  onOpenChange,
  entrepriseId,
  editing,
}: Props): JSX.Element {
  const [step, setStep] = useState<(typeof STEPS)[number]['key']>('infos');
  const upsertPipeline = useUpsertPipeline();
  const { data: activites = [] } = useAllActivites();

  // Pipeline en cours (créé à la fin de l'étape 1)
  const [pipelineId, setPipelineId] = useState<string | null>(editing?.id ?? null);
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [activiteId, setActiviteId] = useState<string | null>(null);
  const [couleur, setCouleur] = useState('#6366f1');
  const [icone, setIcone] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep('infos');
    if (editing) {
      setPipelineId(editing.id);
      setNom(editing.nom);
      setDescription(editing.description ?? '');
      setActiviteId(editing.activite_id);
      setCouleur(editing.couleur);
      setIcone(editing.icone ?? '');
    } else {
      setPipelineId(null);
      setNom('');
      setDescription('');
      setActiviteId(null);
      setCouleur('#6366f1');
      setIcone('');
    }
  }, [open, editing]);

  const activitesEnt = useMemo(
    () => activites.filter((a) => a.entreprise_id === entrepriseId),
    [activites, entrepriseId],
  );

  const handleSaveInfos = async (): Promise<void> => {
    if (!nom.trim()) {
      toast.error('Nom obligatoire');
      return;
    }
    const p = await upsertPipeline.mutateAsync({
      id: pipelineId ?? undefined,
      entreprise_id: entrepriseId,
      activite_id: activiteId,
      nom: nom.trim(),
      description: description || null,
      couleur,
      icone: icone || null,
    });
    setPipelineId(p.id);
    toast.success('Infos enregistrées');
    setStep('etapes');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? `Modifier le pipeline : ${editing.nom}` : 'Nouveau pipeline'}
          </DialogTitle>
          <DialogDescription>
            Configuration no-code en 4 étapes. Vos changements sont enregistrés à chaque section.
          </DialogDescription>
        </DialogHeader>

        <div className="border-b border-border px-6 pb-2">
          <ol className="flex items-center gap-1 text-xs">
            {STEPS.map((s, i) => (
              <li key={s.key} className="flex items-center">
                <button
                  type="button"
                  disabled={i > 0 && !pipelineId}
                  onClick={() => setStep(s.key)}
                  className={cn(
                    'rounded-full px-3 py-1 transition',
                    s.key === step
                      ? 'bg-primary text-primary-foreground'
                      : pipelineId || i === 0
                        ? 'text-muted-foreground hover:bg-secondary'
                        : 'cursor-not-allowed text-muted-foreground/40',
                  )}
                >
                  {i + 1}. {s.label}
                </button>
                {i < STEPS.length - 1 && (
                  <ChevronRight className="mx-1 h-3 w-3 text-muted-foreground" />
                )}
              </li>
            ))}
          </ol>
        </div>

        <DialogBody className="space-y-4">
          {step === 'infos' && (
            <div className="space-y-3">
              <Field label="Nom du pipeline" required>
                <Input
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Ex: Trip Sun — Visa étudiant"
                />
              </Field>
              <Field label="Description">
                <textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Activité rattachée">
                <select
                  value={activiteId ?? ''}
                  onChange={(e) => setActiviteId(e.target.value || null)}
                  className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="">— Aucune —</option>
                  {activitesEnt.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nom}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Couleur">
                <div className="flex flex-wrap gap-2">
                  {COULEURS_PRESET.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setCouleur(c)}
                      className={cn(
                        'h-7 w-7 rounded-full border-2',
                        couleur === c ? 'border-foreground' : 'border-transparent',
                      )}
                      style={{ background: c }}
                    />
                  ))}
                  <Input
                    value={couleur}
                    onChange={(e) => setCouleur(e.target.value)}
                    className="h-7 w-24 text-xs"
                  />
                </div>
              </Field>
            </div>
          )}

          {step === 'etapes' && pipelineId && <EtapesEditor pipelineId={pipelineId} />}
          {step === 'champs' && pipelineId && <ChampsEditor pipelineId={pipelineId} />}
          {step === 'motifs' && pipelineId && <MotifsEditor pipelineId={pipelineId} />}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          {step === 'infos' && (
            <Button onClick={handleSaveInfos} disabled={upsertPipeline.isPending}>
              {upsertPipeline.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Enregistrer & continuer →
            </Button>
          )}
          {step !== 'infos' && (
            <Button
              variant="outline"
              onClick={() => {
                const idx = STEPS.findIndex((s) => s.key === step);
                if (idx > 0) {
                  const prev = STEPS[idx - 1];
                  if (prev) setStep(prev.key);
                }
              }}
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Précédent
            </Button>
          )}
          {step !== 'motifs' && step !== 'infos' && (
            <Button
              onClick={() => {
                const idx = STEPS.findIndex((s) => s.key === step);
                if (idx < STEPS.length - 1) {
                  const next = STEPS[idx + 1];
                  if (next) setStep(next.key);
                }
              }}
            >
              Suivant <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          )}
          {step === 'motifs' && <Button onClick={() => onOpenChange(false)}>Terminer</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block space-y-1 text-xs">
      <span className="text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
    </label>
  );
}

// =================== ÉTAPES ===================
function EtapesEditor({ pipelineId }: { pipelineId: string }): JSX.Element {
  const { data: etapes = [] } = usePipelineEtapes(pipelineId);
  const upsert = useUpsertEtape();
  const remove = useDeleteEtape();
  const reorder = useReorderEtapes();

  const [adding, setAdding] = useState(false);
  const [newNom, setNewNom] = useState('');
  const [newType, setNewType] = useState<EtapeType>('open');
  const [newEmoji, setNewEmoji] = useState('');
  const [newProb, setNewProb] = useState('50');

  const addEtape = async (): Promise<void> => {
    if (!newNom.trim()) return;
    await upsert.mutateAsync({
      pipeline_id: pipelineId,
      nom: newNom.trim(),
      ordre: etapes.length + 1,
      type: newType,
      icone_emoji: newEmoji || null,
      probabilite_defaut: Number(newProb),
    });
    setNewNom('');
    setNewEmoji('');
    setNewProb('50');
    setNewType('open');
    setAdding(false);
    toast.success('Étape ajoutée');
  };

  const move = async (etape: PipelineEtapeRow, delta: number): Promise<void> => {
    const idx = etapes.findIndex((e) => e.id === etape.id);
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= etapes.length) return;
    const reordered = [...etapes];
    const [removedItem] = reordered.splice(idx, 1);
    if (!removedItem) return;
    reordered.splice(newIdx, 0, removedItem);
    await reorder.mutateAsync({ ids: reordered.map((e) => e.id), pipelineId });
  };

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Étapes ({etapes.length})
      </h4>
      <ul className="space-y-1.5">
        {etapes.map((e, idx) => (
          <li
            key={e.id}
            className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-xs"
          >
            <span className="font-mono text-muted-foreground">{idx + 1}.</span>
            <span>{e.icone_emoji ?? '•'}</span>
            <Input
              defaultValue={e.nom}
              onBlur={(ev) =>
                upsert.mutateAsync({
                  id: e.id,
                  pipeline_id: pipelineId,
                  nom: ev.target.value,
                  ordre: e.ordre,
                  type: e.type,
                })
              }
              className="h-7 flex-1"
            />
            <select
              value={e.type}
              onChange={(ev) =>
                upsert.mutateAsync({
                  id: e.id,
                  pipeline_id: pipelineId,
                  nom: e.nom,
                  ordre: e.ordre,
                  type: ev.target.value as EtapeType,
                })
              }
              className="h-7 rounded border border-input bg-background px-1 text-xs"
            >
              <option value="open">Ouverte</option>
              <option value="won">Gagnée</option>
              <option value="lost">Perdue</option>
            </select>
            <Input
              type="number"
              min={0}
              max={100}
              defaultValue={e.probabilite_defaut}
              onBlur={(ev) =>
                upsert.mutateAsync({
                  id: e.id,
                  pipeline_id: pipelineId,
                  nom: e.nom,
                  ordre: e.ordre,
                  type: e.type,
                  probabilite_defaut: Number(ev.target.value),
                })
              }
              className="h-7 w-16 text-xs"
            />
            <Badge variant="outline">{e.probabilite_defaut}%</Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={idx === 0}
              onClick={() => void move(e, -1)}
            >
              <ArrowUp className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={idx === etapes.length - 1}
              onClick={() => void move(e, 1)}
            >
              <ArrowDown className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive"
              onClick={async () => {
                try {
                  await remove.mutateAsync(e.id);
                  toast.success('Étape supprimée');
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </li>
        ))}
      </ul>
      {adding ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-2 text-xs">
          <Input
            placeholder="Nom de l'étape"
            value={newNom}
            onChange={(e) => setNewNom(e.target.value)}
            className="h-7 flex-1"
          />
          <Input
            placeholder="Emoji"
            value={newEmoji}
            onChange={(e) => setNewEmoji(e.target.value)}
            className="h-7 w-16"
          />
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as EtapeType)}
            className="h-7 rounded border border-input bg-background px-1 text-xs"
          >
            <option value="open">Ouverte</option>
            <option value="won">Gagnée</option>
            <option value="lost">Perdue</option>
          </select>
          <Input
            type="number"
            min={0}
            max={100}
            value={newProb}
            onChange={(e) => setNewProb(e.target.value)}
            className="h-7 w-16"
          />
          <Button size="sm" onClick={() => void addEtape()}>
            Ajouter
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-3 w-3" /> Ajouter une étape
        </Button>
      )}
    </div>
  );
}

// =================== CHAMPS ===================
function ChampsEditor({ pipelineId }: { pipelineId: string }): JSX.Element {
  const { data: champs = [] } = usePipelineChamps(pipelineId);
  const upsert = useUpsertChamp();
  const remove = useDeleteChamp();

  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState<ChampCustomType>('text');
  const [newOptions, setNewOptions] = useState('');
  const [newSection, setNewSection] = useState('');
  const [newObligatoire, setNewObligatoire] = useState(false);

  const slug = (s: string): string =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 40);

  const addChamp = async (): Promise<void> => {
    if (!newLabel.trim()) return;
    const opts =
      newType === 'select' || newType === 'multiselect'
        ? newOptions
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : null;
    await upsert.mutateAsync({
      pipeline_id: pipelineId,
      nom_champ: slug(newLabel),
      label: newLabel.trim(),
      type: newType,
      options: opts,
      obligatoire: newObligatoire,
      ordre: champs.length + 1,
      section: newSection || null,
    });
    setNewLabel('');
    setNewOptions('');
    setNewSection('');
    setNewObligatoire(false);
    setNewType('text');
    setAdding(false);
    toast.success('Champ ajouté');
  };

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Champs custom ({champs.length})
      </h4>
      <ul className="space-y-1.5">
        {champs.map((c: PipelineChampCustomRow) => (
          <li
            key={c.id}
            className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-xs"
          >
            <span className="line-clamp-1 flex-1 font-medium">{c.label}</span>
            <Badge variant="outline">{c.type}</Badge>
            {c.obligatoire && <Badge variant="warning">Obligatoire</Badge>}
            {c.section && <span className="text-muted-foreground">/ {c.section}</span>}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive"
              onClick={async () => {
                await remove.mutateAsync(c.id);
                toast.success('Champ supprimé');
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </li>
        ))}
      </ul>
      {adding ? (
        <div className="space-y-2 rounded-md border border-dashed border-border p-3 text-xs">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Label affiché" required>
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                className="h-8"
              />
            </Field>
            <Field label="Type">
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as ChampCustomType)}
                className="h-8 w-full rounded border border-input bg-background px-1"
              >
                {CHAMP_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Section (regroupement)">
              <Input
                value={newSection}
                onChange={(e) => setNewSection(e.target.value)}
                className="h-8"
              />
            </Field>
            <label className="flex items-center gap-2 self-end">
              <input
                type="checkbox"
                checked={newObligatoire}
                onChange={(e) => setNewObligatoire(e.target.checked)}
              />
              Obligatoire
            </label>
            {(newType === 'select' || newType === 'multiselect') && (
              <Field label="Options (séparées par virgule)">
                <Input
                  value={newOptions}
                  onChange={(e) => setNewOptions(e.target.value)}
                  placeholder="Option 1, Option 2, Option 3"
                  className="h-8"
                />
              </Field>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
              Annuler
            </Button>
            <Button size="sm" onClick={() => void addChamp()}>
              Ajouter
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-3 w-3" /> Ajouter un champ custom
        </Button>
      )}
    </div>
  );
}

// =================== MOTIFS ===================
function MotifsEditor({ pipelineId }: { pipelineId: string }): JSX.Element {
  const { data: motifs = [] } = usePipelineMotifsPerte(pipelineId);
  const upsert = useUpsertMotifPerte();
  const remove = useDeleteMotifPerte();
  const [newLibelle, setNewLibelle] = useState('');

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Motifs de perte ({motifs.length})
      </h4>
      <ul className="space-y-1.5">
        {motifs.map((m: PipelineMotifPerteRow) => (
          <li
            key={m.id}
            className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-xs"
          >
            <span className="flex-1">{m.libelle}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive"
              onClick={async () => {
                await remove.mutateAsync(m.id);
                toast.success('Motif supprimé');
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Input
          placeholder="Nouveau motif (ex: Visa refusé)"
          value={newLibelle}
          onChange={(e) => setNewLibelle(e.target.value)}
          className="h-8"
        />
        <Button
          size="sm"
          onClick={async () => {
            if (!newLibelle.trim()) return;
            await upsert.mutateAsync({
              pipeline_id: pipelineId,
              libelle: newLibelle.trim(),
              ordre: motifs.length + 1,
            });
            setNewLibelle('');
            toast.success('Motif ajouté');
          }}
        >
          <Plus className="h-3 w-3" /> Ajouter
        </Button>
      </div>
    </div>
  );
}
